import { t } from "@galaxy-io/dls/theme/tokens/t";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { styled } from "@linaria/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

export const TrackingActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${t.space[8]};

`;

/** Both tracking tables share the confirmation, mutation and cache rollback. */
export function useRemoveTrackedItem<T extends { id: string }>(
  provider: string,
  kind: string,
  queryKey: string,
  itemName: (row: T) => string,
) {
  const [item, setItem] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const { toast } = useToast();
  async function remove() {
    if (!item || busy) return;
    setBusy(true);
    await client.cancelQueries({ queryKey: [queryKey] });
    const previous = client.getQueryData<T[]>([queryKey]);
    client.setQueryData(
      [queryKey],
      previous?.filter((row) => row.id !== item.id && itemName(row) !== item.name),
    );
    try {
      await unwrap(
        apiClient().POST("/resources/untrack", {
          body: { sourceId: item.id, provider, kind },
        }),
      );
      setItem(null);
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Tracking removed",
        description: `Collection for ${item.name} stopped. Existing signals are kept.`,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } catch (error) {
      client.setQueryData([queryKey], previous);
      throw error;
    } finally {
      await Promise.all(
        [queryKey, "integrations", "collection-sources", "integration-audit"].map((key) =>
          client.invalidateQueries({ queryKey: [key] }),
        ),
      );
      setBusy(false);
    }
  }
  return {
    busy,
    request: (id: string, name: string) => setItem({ id, name }),
    dialog: (
      <ConfirmAction
        isOpen={!!item}
        header={`Stop tracking ${item?.name ?? "resource"}?`}
        description={
          <>Collection stops across all connections. Existing signals and their source are kept.</>
        }
        label="Remove"
        isDestructive
        onOpenChange={(next) => {
          if (!next) (() => setItem(null))();
        }}
        onConfirm={async () => remove()}
      />
    ),
  };
}
